import { useEffect, useState } from 'react';

/**
 * Dev toggle for the Index Funds admin page v2 redesign. Mirrors useStudioDevAccess:
 * a `?indexFundsV2=1|0` URL param wins and is persisted to localStorage, otherwise the stored flag
 * is used. The Data > Index Funds tab renders a Classic/v2 switch that flips it. Self-contained (no
 * redux, no backend) so the whole thing rips out in one delete.
 */
const STORAGE_KEY = 'vega.dev.indexFundsV2';
const PARAM = 'indexFundsV2';

const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const resolveInitial = (): boolean => {
  try {
    const value = new URLSearchParams(window.location.search).get(PARAM);
    if (value === '0' || value === 'false') {
      window.localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    if (value !== null) {
      window.localStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
  } catch {
    /* ignore quota / privacy-mode failures */
  }
  return readStored();
};

export function useIndexFundsV2Access(): [boolean, (value: boolean) => void] {
  const [enabled, setEnabled] = useState<boolean>(resolveInitial);

  useEffect(() => {
    // Keep multiple mounts / other tabs in sync if the flag changes.
    const sync = () => setEnabled(readStored());
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const set = (value: boolean) => {
    try {
      if (value) window.localStorage.setItem(STORAGE_KEY, '1');
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setEnabled(value);
  };

  return [enabled, set];
}
